import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaShieldAlt,
  FaLock,
  FaCreditCard,
  FaUserShield,
  FaEnvelope,
  FaPhone,
  FaUser,
  FaCog,
  FaHistory,
  FaTrash,
  FaEye,
  FaEdit,
  FaDownload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle, FaTimes
} from 'react-icons/fa';

const PrivacyPolicy = () => {
  const [currentSection, setCurrentSection] = useState('overview');
  const [lastUpdated] = useState('January 15, 2024');

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'collection', title: 'Data Collection' },
    { id: 'usage', title: 'How We Use Data' },
    { id: 'security', title: 'Payment Security' },
    { id: 'thirdparty', title: 'Third-Party Services' },
    { id: 'rights', title: 'Your Rights' },
    { id: 'updates', title: 'Policy Updates' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setCurrentSection(sectionId);
    }
  };

  const downloadPolicy = () => {
    // In a real app, this would download a PDF version
    alert('PDF version of Privacy Policy will be downloaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-3">Privacy Policy</h1>
              <p className="text-blue-100 text-lg">
                Your privacy and data security are our top priorities
              </p>
            </div>
            <div className="mt-6 md:mt-0 flex items-center space-x-4">
              <span className="bg-white/20 px-4 py-2 rounded-full text-sm">
                Last Updated: {lastUpdated}
              </span>
              <button
                onClick={downloadPolicy}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors flex items-center"
              >
                <FaDownload className="mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <div className="flex items-center mb-6">
                <FaShieldAlt className="text-blue-600 text-xl mr-3" />
                <h2 className="text-xl font-bold text-gray-800">Quick Navigation</h2>
              </div>
              
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                      currentSection === section.id
                        ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-3 ${
                        currentSection === section.id ? 'bg-blue-600' : 'bg-gray-300'
                      }`}></div>
                      {section.title}
                    </div>
                  </button>
                ))}
              </nav>

              <div className="mt-8 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex items-start">
                  <FaCheckCircle className="text-green-500 mt-1 mr-3" />
                  <div>
                    <h4 className="font-bold text-green-800 mb-1">SSL Secured</h4>
                    <p className="text-sm text-green-700">All data transmitted is encrypted</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            {/* Overview Section */}
            <div id="overview" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-xl mr-4">
                  <FaShieldAlt className="text-blue-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Our Commitment to Privacy</h2>
                  <p className="text-gray-600 mt-1">Introduction & Overview</p>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  At <strong>Anusaya Healthcare</strong>, we are committed to protecting your privacy 
                  and ensuring the security of your personal information. This Privacy Policy 
                  explains how we collect, use, disclose, and safeguard your information when 
                  you visit our website and use our services.
                </p>
                
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center mb-3">
                    <FaExclamationTriangle className="text-blue-600 mr-3" />
                    <h4 className="font-bold text-blue-800">Important Notice</h4>
                  </div>
                  <p className="text-blue-700">
                    By using our services, you consent to the data practices described in this policy. 
                    If you do not agree with the terms of this policy, please do not access our services.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Collection Section */}
            <div id="collection" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-purple-100 rounded-xl mr-4">
                  <FaUser className="text-purple-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Information We Collect</h2>
                  <p className="text-gray-600 mt-1">Personal & Non-Personal Data</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="p-4 border border-gray-200 rounded-xl hover:border-purple-300 transition-colors">
                  <div className="flex items-center mb-3">
                    <FaUser className="text-purple-500 mr-2" />
                    <h4 className="font-bold text-gray-800">Personal Information</h4>
                  </div>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <FaUser className="text-gray-400 mr-2" size={12} />
                      Full Name
                    </li>
                    <li className="flex items-center">
                      <FaEnvelope className="text-gray-400 mr-2" size={12} />
                      Email Address
                    </li>
                    <li className="flex items-center">
                      <FaPhone className="text-gray-400 mr-2" size={12} />
                      Phone Number
                    </li>
                    <li className="flex items-center">
                      <FaHistory className="text-gray-400 mr-2" size={12} />
                      Date of Birth
                    </li>
                    <li className="flex items-center">
                      <FaCog className="text-gray-400 mr-2" size={12} />
                      Medical Information (if applicable)
                    </li>
                  </ul>
                </div>

                <div className="p-4 border border-gray-200 rounded-xl hover:border-blue-300 transition-colors">
                  <div className="flex items-center mb-3">
                    <FaCreditCard className="text-blue-500 mr-2" />
                    <h4 className="font-bold text-gray-800">Payment Information</h4>
                  </div>
                  <div className="text-gray-600 space-y-2">
                    <p className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" />
                      Processed securely via Razorpay
                    </p>
                    <p className="flex items-center">
                      <FaExclamationTriangle className="text-yellow-500 mr-2" />
                      We do NOT store card/UPI details
                    </p>
                    <p className="text-sm text-gray-500 mt-3">
                      All payment data is encrypted and handled by Razorpay, our PCI-DSS compliant payment partner.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl">
                <div className="flex items-start">
                  <FaInfoCircle className="text-purple-600 mt-1 mr-3" />
                  <div>
                    <h4 className="font-bold text-purple-800 mb-1">How We Collect Information</h4>
                    <ul className="text-purple-700 list-disc pl-5 space-y-1">
                      <li>When you register for an account</li>
                      <li>When you book appointments or services</li>
                      <li>When you make payments</li>
                      <li>When you contact our support team</li>
                      <li>Through cookies and similar technologies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* How We Use Data Section */}
            <div id="usage" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-green-100 rounded-xl mr-4">
                  <FaCog className="text-green-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">How We Use Your Data</h2>
                  <p className="text-gray-600 mt-1">Purpose of Data Processing</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center mb-3">
                    <FaCreditCard className="text-green-600 mr-2" />
                    <h4 className="font-bold text-green-800">Payment Processing</h4>
                  </div>
                  <p className="text-green-700 text-sm">
                    To process your payments securely and provide transaction receipts
                  </p>
                </div>

                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-center mb-3">
                    <FaEnvelope className="text-blue-600 mr-2" />
                    <h4 className="font-bold text-blue-800">Customer Support</h4>
                  </div>
                  <p className="text-blue-700 text-sm">
                    To respond to your inquiries and provide quality customer service
                  </p>
                </div>

                <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                  <div className="flex items-center mb-3">
                    <FaUser className="text-purple-600 mr-2" />
                    <h4 className="font-bold text-purple-800">Communication</h4>
                  </div>
                  <p className="text-purple-700 text-sm">
                    To send appointment reminders, service updates, and important notices
                  </p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <h4 className="font-bold text-gray-800 mb-2">Additional Uses:</h4>
                <ul className="text-gray-600 space-y-1">
                  <li>• To improve our services and user experience</li>
                  <li>• To comply with legal obligations</li>
                  <li>• To prevent fraudulent activities</li>
                  <li>• To analyze usage patterns (anonymous data only)</li>
                </ul>
              </div>
            </div>

            {/* Payment Security Section */}
            <div id="security" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-red-100 rounded-xl mr-4">
                  <FaLock className="text-red-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Payment Security</h2>
                  <p className="text-gray-600 mt-1">Secure Payment Processing</p>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
                  <FaCreditCard className="text-red-600 text-2xl mr-4" />
                  <div>
                    <h4 className="font-bold text-red-800 text-lg">Payments via Razorpay</h4>
                    <p className="text-red-700">
                      All payments are securely processed through Razorpay, India's leading 
                      payment gateway. We do not have access to your complete payment details.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">We DO NOT Store:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <FaTimes className="text-red-500 mr-2" />
                      Credit/Debit Card Numbers
                    </li>
                    <li className="flex items-center">
                      <FaTimes className="text-red-500 mr-2" />
                      CVV/CVC Codes
                    </li>
                    <li className="flex items-center">
                      <FaTimes className="text-red-500 mr-2" />
                      UPI PINs
                    </li>
                    <li className="flex items-center">
                      <FaTimes className="text-red-500 mr-2" />
                      Net Banking Passwords
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Security Features:</h4>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" />
                      256-bit SSL Encryption
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" />
                      PCI-DSS Compliance
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" />
                      Two-Factor Authentication
                    </li>
                    <li className="flex items-center">
                      <FaCheckCircle className="text-green-500 mr-2" />
                      Regular Security Audits
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Third-Party Services */}
            <div id="thirdparty" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-yellow-100 rounded-xl mr-4">
                  <FaUserShield className="text-yellow-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Third-Party Services</h2>
                  <p className="text-gray-600 mt-1">Trusted Service Providers</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 border border-yellow-200 rounded-xl bg-yellow-50">
                  <h4 className="font-bold text-yellow-800 mb-2">Razorpay</h4>
                  <p className="text-yellow-700 mb-2">
                    Our payment processing partner that handles all financial transactions.
                  </p>
                  <a 
                    href="https://razorpay.com/privacy/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Razorpay's Privacy Policy →
                  </a>
                </div>

                <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                  <h4 className="font-bold text-gray-800 mb-2">Analytics Tools</h4>
                  <p className="text-gray-600 mb-2">
                    We may use analytics services (like Google Analytics) to understand 
                    how users interact with our website. These services collect anonymous data.
                  </p>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View Google's Privacy Policy →
                  </a>
                </div>
              </div>
            </div>

            {/* User Rights */}
            <div id="rights" className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-teal-100 rounded-xl mr-4">
                  <FaEye className="text-teal-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Your Rights</h2>
                  <p className="text-gray-600 mt-1">Control Over Your Data</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 bg-teal-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FaEye className="text-teal-600 mr-2" />
                      <h4 className="font-bold text-teal-800">Right to Access</h4>
                    </div>
                    <p className="text-teal-700 text-sm">
                      You can request a copy of the personal data we hold about you.
                    </p>
                  </div>

                  <div className="p-4 bg-teal-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FaEdit className="text-teal-600 mr-2" />
                      <h4 className="font-bold text-teal-800">Right to Correction</h4>
                    </div>
                    <p className="text-teal-700 text-sm">
                      You can request corrections to inaccurate or incomplete data.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-teal-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FaTrash className="text-teal-600 mr-2" />
                      <h4 className="font-bold text-teal-800">Right to Deletion</h4>
                    </div>
                    <p className="text-teal-700 text-sm">
                      You can request deletion of your personal data under certain conditions.
                    </p>
                  </div>

                  <div className="p-4 bg-teal-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <FaCog className="text-teal-600 mr-2" />
                      <h4 className="font-bold text-teal-800">Right to Object</h4>
                    </div>
                    <p className="text-teal-700 text-sm">
                      You can object to certain types of data processing.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-200">
                <h4 className="font-bold text-teal-800 mb-2">How to Exercise Your Rights</h4>
                <p className="text-teal-700">
                  To exercise any of these rights, please contact our Data Protection Officer at:
                  <br />
                  <strong>Email:</strong> privacy@Anusaya.com | 
                  <strong>Phone:</strong> +91 98765 43210
                </p>
              </div>
            </div>

            {/* Policy Updates */}
            <div id="updates" className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-indigo-100 rounded-xl mr-4">
                  <FaHistory className="text-indigo-600 text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Policy Updates</h2>
                  <p className="text-gray-600 mt-1">Keeping You Informed</p>
                </div>
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy from time to time to reflect changes 
                  in our practices, technology, legal requirements, or other factors.
                </p>

                <div className="p-4 bg-indigo-50 rounded-xl mb-4">
                  <h4 className="font-bold text-indigo-800 mb-2">Update Notification</h4>
                  <p className="text-indigo-700">
                    When we make significant changes to this policy, we will:
                  </p>
                  <ul className="text-indigo-700 list-disc pl-5 mt-2 space-y-1">
                    <li>Post the updated policy on our website</li>
                    <li>Update the "Last Updated" date at the top of this page</li>
                    <li>Notify registered users via email (for major changes)</li>
                  </ul>
                </div>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-bold text-gray-800 mb-2">Contact Information</h4>
                  <p className="text-gray-700">
                    If you have any questions about this Privacy Policy or our data practices, 
                    please contact us:
                  </p>
                  <div className="mt-2 space-y-1">
                    <p className="text-gray-600">
                      <strong>Email:</strong> privacy@Anusaya.com
                    </p>
                    <p className="text-gray-600">
                      <strong>Phone:</strong> +91 98765 43210
                    </p>
                    <p className="text-gray-600">
                      <strong>Address:</strong> 123 Medical Plaza, Pune, Maharashtra - 411001
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            This Privacy Policy is effective as of {lastUpdated}. By using our services, 
            you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;